import { type FormEvent, type RefObject, useRef, useState } from 'react';
import { type NewFamilyMember, parseFamilyBirthDate } from '../familyManagement';
import { FamilyDialog } from './FamilyDialog';

const emptyForm = {
  firstName: '', familyName: '', dateOfBirth: '', streetAndNumber: '',
  zipCode: '', town: '', country: 'Schweiz', relationship: '', relationshipDetails: '',
};
type FieldName = keyof typeof emptyForm;

export function AddFamilyMemberDialog({ onClose, onAdd, unavailableReason, returnFocusRef }: {
  onClose: () => void;
  onAdd: (input: NewFamilyMember) => Promise<void>;
  unavailableReason?: string;
  returnFocusRef: RefObject<HTMLButtonElement>;
}) {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);

  const update = (field: FieldName, value: string) => {
    setForm((current) => ({ ...current, [field]: value,
      ...(field === 'relationship' && value !== 'other' ? { relationshipDetails: '' } : {}),
    }));
    setErrors((current) => ({ ...current, [field]: undefined }));
    setError('');
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting.current) return;
    const nextErrors: Partial<Record<FieldName, string>> = {};
    if (!form.firstName.trim()) nextErrors.firstName = 'Bitte geben Sie den Vornamen ein.';
    if (!form.familyName.trim()) nextErrors.familyName = 'Bitte geben Sie den Nachnamen ein.';
    const dateOfBirth = parseFamilyBirthDate(form.dateOfBirth);
    if (!dateOfBirth) nextErrors.dateOfBirth = 'Bitte geben Sie ein gültiges, nicht zukünftiges Geburtsdatum im Format TT.MM.JJJJ ein.';
    if (!form.relationship) nextErrors.relationship = 'Bitte wählen Sie die Beziehung aus.';
    if (form.relationship === 'other' && !form.relationshipDetails.trim()) {
      nextErrors.relationshipDetails = 'Bitte geben Sie die Beziehung an.';
    }
    setErrors(nextErrors);
    setError('');
    if (Object.keys(nextErrors).length) {
      const field = Object.keys(nextErrors)[0];
      event.currentTarget.querySelector<HTMLElement>(`[name="${field}"]`)?.focus();
      return;
    }
    if (unavailableReason) { setError(unavailableReason); return; }
    submitting.current = true;
    setBusy(true);
    try {
      await onAdd({
        firstName: form.firstName.trim(), familyName: form.familyName.trim(), dateOfBirth: dateOfBirth!,
        streetAndNumber: form.streetAndNumber.trim(), zipCode: form.zipCode.trim(), town: form.town.trim(),
        country: form.country.trim(), relationship: form.relationship as NewFamilyMember['relationship'],
        ...(form.relationship === 'other' ? { relationshipDetails: form.relationshipDetails.trim() } : {}),
      });
      onClose();
    } catch {
      setError('Das Familienmitglied konnte nicht hinzugefügt werden. Bitte versuchen Sie es erneut oder wenden Sie sich an die Praxis.');
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  const field = (name: FieldName, label: string, required = false, placeholder?: string) => (
    <div className="family-dialog__field">
      <label htmlFor={`family-${name}`}>{label}{required ? ' *' : ''}</label>
      <input id={`family-${name}`} name={name} value={form[name]} required={required}
        onChange={(event) => update(name, event.target.value)} placeholder={placeholder}
        maxLength={name === 'dateOfBirth' ? 10 : name === 'zipCode' ? 16 : 160}
        autoComplete="off" disabled={busy} data-initial-focus={name === 'firstName' ? '' : undefined}
        aria-invalid={Boolean(errors[name])} aria-describedby={errors[name] ? `family-error-${name}` : undefined} />
      {errors[name] && <span className="family-dialog__field-error" id={`family-error-${name}`}>{errors[name]}</span>}
    </div>
  );

  return (
    <FamilyDialog title="Familienmitglied hinzufügen" busy={busy} onClose={onClose} returnFocusRef={returnFocusRef}
      description="Erfassen Sie die Angaben des Familienmitglieds, das Sie in Ihrem Patientenportal verwalten möchten.">
      {unavailableReason && <p className="family-dialog__notice">{unavailableReason}</p>}
      <form onSubmit={submit} noValidate>
        <p className="family-dialog__required">* Pflichtfelder</p>
        <fieldset disabled={busy}>
          <legend>Persönliche Angaben</legend>
          <div className="family-dialog__grid">
            {field('firstName', 'Vorname', true)}
            {field('familyName', 'Nachname', true)}
          </div>
          {field('dateOfBirth', 'Geburtsdatum', true, 'TT.MM.JJJJ')}
        </fieldset>
        <fieldset disabled={busy}>
          <legend>Adresse</legend>
          {field('streetAndNumber', 'Strasse und Hausnummer')}
          <div className="family-dialog__grid family-dialog__grid--address">
            {field('zipCode', 'Postleitzahl')}
            {field('town', 'Ort')}
          </div>
          {field('country', 'Land')}
        </fieldset>
        <fieldset disabled={busy}>
          <legend>Beziehung zum Patienten</legend>
          <div className="family-dialog__field">
            <label htmlFor="family-relationship">Beziehung *</label>
            <select id="family-relationship" name="relationship" value={form.relationship} required
              onChange={(event) => update('relationship', event.target.value)}
              aria-invalid={Boolean(errors.relationship)}
              aria-describedby={errors.relationship ? 'family-error-relationship' : undefined}>
              <option value="">Bitte wählen</option>
              <option value="daughter">Tochter</option>
              <option value="son">Sohn</option>
              <option value="other">Andere</option>
            </select>
            {errors.relationship && <span className="family-dialog__field-error" id="family-error-relationship">{errors.relationship}</span>}
          </div>
          {form.relationship === 'other' && field('relationshipDetails', 'Beziehung angeben', true)}
        </fieldset>
        {error && <p role="alert" className="family-dialog__error">{error}</p>}
        <div className="family-dialog__actions">
          <button type="button" className="family-dialog__secondary" onClick={onClose} disabled={busy}>Abbrechen</button>
          <button type="submit" className="family-dialog__primary" disabled={busy}>
            {busy ? 'Wird hinzugefügt…' : 'Familienmitglied hinzufügen'}
          </button>
        </div>
      </form>
    </FamilyDialog>
  );
}
